/**
 * Карта: the map plus the panels over it.
 *
 * LAYOUT. Two full-height panels flank a full-bleed map, and everything else rides IN the
 * map's own corners:
 *
 *   left    — the field roster, full height (`.k-sidebar`), the widest thing after the map;
 *   right   — the console, three numbered parts in the agronomist's order;
 *   map top-left     — the contour tools, the coordinate search, the basemap switch;
 *   map top-right    — the dated-imagery control and its calendar.
 *
 * The two map-corner groups are passed to `MapCanvas` as `controls` instead of being laid
 * over it in a strip of our own. The strip is what put our date bar on top of Leaflet's zoom
 * buttons: two layout systems aimed at the same pixels, and whichever won did so by z-index
 * accident. Leaflet owns the corners now (map/controls.ts), including where its own zoom,
 * scale and attribution sit, and no corner holds both ours and theirs. This screen still
 * names a corner and nothing else — it has never seen an `L.Map` and still does not.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as api from "../api/client";
import { CoordSearch } from "../components/CoordSearch";
import { DrawTools, type DrawMode, type MarkerTarget } from "../components/DrawTools";
import { FieldList } from "../components/FieldList";
import { BasemapSwitch } from "../components/BasemapSwitch";
import { PreviewControl } from "../components/PreviewControl";
import { Console } from "../components/Console";
import type { NotesPanel } from "../components/FieldNotes";
import { NoteModal, type NoteDraftText } from "../components/NoteModal";
import { noteName, photosOfNote } from "../lib/notes";
import { todayIso } from "../lib/format";
import { T } from "../strings";
import { useNotesUi } from "../state/useNotesUi";
import { PIN_FLASH_MS } from "../map/pins";
import { MapCanvas } from "../map/MapCanvas";
import type { DrawController } from "../map/geoman";
import { importDroppedFile, type DropOutcome } from "../map/drop";
import type { SearchPoint } from "../map/markers";
import type { SearchTarget } from "../lib/places";
import { checkGeometry } from "../lib/validateGeometry";
import { useCoordinatePaste } from "../hooks/useCoordinatePaste";
import { useBasemap } from "../hooks/useBasemap";
import { useDevMode } from "../hooks/useDevMode";
import { usePreview } from "../hooks/usePreview";
import type { Theme } from "../hooks/useTheme";
import type { Viewport } from "../lib/viewport";
import type { Workspace } from "../state/useWorkspace";
import type { RunFlags } from "../api/types";

interface Props {
  workspace: Workspace;
  theme: Theme;
  animate: boolean;
}

/**
 * How long the answer to a dropped file stands. The roster's «Сохранено» number, deliberately
 * the same one: two notices on one screen that fade at different speeds read as a bug.
 */
export const DROP_NOTICE_MS = 4000;

export function MapScreen({ workspace, theme, animate }: Props) {
  const [mode, setMode] = useState<DrawMode>("idle");
  const [clearToken, setClearToken] = useState(0);
  /**
   * Взведённая постановка места. Живёт здесь, потому что взводят её ДВОЕ — тулбар карты и
   * карточка в панели заметок, — а тратит один: карта. Общий родитель у всех троих этот экран.
   */
  const [markerTarget, setMarkerTarget] = useState<MarkerTarget | null>(null);
  /** What came back from the last file dropped on the map. */
  const [drop, setDrop] = useState<DropOutcome>({ note: null, error: null });
  // Where the map is looking. The imagery layer is bbox-scoped now, so this — and not the
  // selected field — is what decides which dates exist (docs/MAP_IMAGERY_RESEARCH.md §2).
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const onViewportChange = useCallback((next: Viewport) => setViewport(next), []);
  const preview = usePreview(workspace.selectedFieldId, workspace.activeRun, viewport);
  // Разрез поверхностей проходит и здесь: числа слоя снимка адресованы НАМ, поэтому живут
  // под календарём и только при включённом служебном режиме. Счётчик живых слоёв приходит
  // с карты — предел «не больше двух» иначе пришлось бы принимать на веру.
  const { devMode } = useDevMode();
  const [previewLayers, setPreviewLayers] = useState(0);

  // --- drawing: undo/redo, geometry checks, coordinate search --------------
  const [history, setHistory] = useState({ canUndo: false, canRedo: false });
  const [searchPoint, setSearchPoint] = useState<SearchPoint | null>(null);
  const draw = useRef<DrawController | null>(null);

  // Checked on every change rather than at save time: a bow-tie ring draws quietly and
  // reports a WRONG area, and the area is what the whole cost guard rests on.
  const check = useMemo(() => checkGeometry(workspace.draft?.geometry ?? null), [workspace.draft]);

  const onHistoryChange = useCallback(
    (canUndo: boolean, canRedo: boolean) => setHistory({ canUndo, canRedo }),
    [],
  );
  const onDrawReady = useCallback((controller: DrawController | null) => {
    draw.current = controller;
  }, []);
  // The token is what makes searching the same coordinates twice fly there twice. The target
  // may carry a box and a name (a geocoded place) or be a bare pair — the map reads both.
  const onFound = useCallback(
    (target: SearchTarget) =>
      setSearchPoint((current) => ({ ...target, token: (current?.token ?? 0) + 1 })),
    [],
  );
  // Ctrl+V anywhere but a text field. The cheapest way into a district there is, and the
  // reason the search box may collapse to an icon without the common case getting worse.
  useCoordinatePaste(onFound);
  const { basemap, setBasemap } = useBasemap();

  // A MODE ABOUT A FIELD DIES WITH THE SELECTION, and so does the point drafted in it. The
  // toolbar only ever ended the mode when the selection became NOTHING; selecting another
  // field left both standing, and the next «Сохранить» posted the draft's coordinates — over
  // the old field — to the new one. Neighbouring fields are 500 m apart: the marker would
  // have landed on somebody else's ground without a word. Clearing the mode is enough to take
  // the draft with it: the draft lives in MapCanvas's effect, which is keyed on the mode.
  const selectedFieldId = workspace.selectedFieldId;
  useEffect(() => {
    setMarkerTarget(null);
  }, [selectedFieldId]);

  /**
   * Состояние вкладки «Заметки», поднятое над консолью: черновик, открытый редактор,
   * раскрытые карточки и зеркало режима постановки. Живёт здесь, потому что неактивная
   * панель размонтируется, а набранный текст переживать это обязан.
   */
  const notesUi = useNotesUi(selectedFieldId);

  /**
   * ТРЕТЬЕ ИЗ ТРЁХ ГАШЕНИЙ РЕЖИМА ПОСТАНОВКИ: заметка исчезла.
   *
   * Первые два — Esc/«Отмена» и смена поля — стоят рядом, явными обработчиками. Это гасится
   * здесь, потому что причина приходит снаружи: заметку удалили из другой вкладки, и перечит
   * принёс список без неё. Без этой строки карта осталась бы ждать клика для точки, которой
   * нет, а следующий клик ушёл бы PATCH-ем в 404. Уход на вкладку «Анализ» режим при этом НЕ
   * гасит: он про карту, а не про то, что сейчас показано в колонке.
   */
  const placingNoteId = markerTarget?.kind === "place" ? markerTarget.noteId : null;
  const notesOfSelected = workspace.markersOfSelected;
  useEffect(() => {
    if (placingNoteId === null) return;
    if (notesOfSelected.some((marker) => marker.id === placingNoteId)) return;
    setMarkerTarget(null);
    notesUi.setPlacingNoteId(null);
  }, [placingNoteId, notesOfSelected, notesUi]);
  /**
   * «Заметки N» в карточке поля ростера. Токен, а не булево: вкладка уже может стоять там,
   * куда её просят поставить, и без счётчика второе нажатие подряд неотличимо от первого.
   */
  const [notesTabToken, setNotesTabToken] = useState(0);
  /**
   * Снять маркер-черновик с карты. Токен, а не булево: второй отказ подряд обязан снять
   * маркер так же, как первый, а по значению эти два события неотличимы.
   */
  const [draftToken, setDraftToken] = useState(0);
  const dropDraftPin = useCallback(() => setDraftToken((token) => token + 1), []);
  /**
   * Что набрано в правой колонке модалки. В режиме СОЗДАНИЯ редактор открыт всегда — заметки
   * ещё нет, и показывать в чтении нечего.
   */
  const [modalEditing, setModalEditing] = useState<NoteDraftText | null>(null);

  /**
   * ОКНО ОТКРЫЛОСЬ — РЕДАКТОР ЗАВОДИТСЯ СРАЗУ ИЛИ НЕ ЗАВОДИТСЯ ВОВСЕ.
   *
   * Создание всегда открыто в правке: заметки ещё нет, и показывать в чтении нечего. У
   * существующей редактор открыт только по карандашу — остальные входы ведут в чтение.
   */
  const draftText = notesUi.draft?.text;
  const draftDate = notesUi.draft?.date;
  const openEditing = notesUi.open?.editing;
  const openNoteId = notesUi.open?.noteId;
  useEffect(() => {
    if (draftText !== undefined && draftDate !== undefined) {
      setModalEditing((current) => current ?? { text: draftText, date: draftDate });
      return;
    }
    if (openNoteId === undefined) {
      setModalEditing(null);
      return;
    }
    if (!openEditing) return;
    const note = workspace.markersOfSelected.find((marker) => marker.id === openNoteId);
    if (note === undefined) return;
    setModalEditing((current) => current ?? { text: note.note, date: note.happened_on });
    // `markersOfSelected` намеренно не в зависимостях: редактор заводится на ОТКРЫТИЕ, а не
    // на приход новых данных, иначе каждая загруженная фотография переоткрывала бы его.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftText, draftDate, openNoteId, openEditing]);

  /**
   * СОХРАНЕНИЕ ИДЁТ ДВУМЯ ПУТЯМИ, И ЭТО ОДНА ФОРМА. Создание — POST заметки, а следом файлы,
   * накопленные до неё: отправить их было некуда, пока у заметки не было id. Правка — один
   * PATCH `{note, happened_on}`: правка даты ЕСТЬ правка заметки, отдельной ручки у неё нет.
   */
  const saveNote = useCallback(
    async (text: string, happenedOn: string) => {
      const fieldId = workspace.selectedFieldId;
      if (fieldId === null) return { ok: false, message: null };
      const draft = notesUi.draft;
      if (draft !== null) {
        const place = draft.place;
        const created = await workspace.addMarker(fieldId, {
          note: text,
          happened_on: happenedOn,
          lon: place?.lon ?? null,
          lat: place?.lat ?? null,
        });
        if (!created.ok) return { ok: false, message: created.message ?? null };
        if (created.markerId !== undefined && draft.files.length > 0) {
          workspace.uploadPhotos(fieldId, draft.files, created.markerId);
        }
        notesUi.setDraft(null);
        notesUi.setOpen(null);
        setModalEditing(null);
        // Заметка есть — режиму больше нечего ждать, а черновик снимается: настоящий маркер
        // придёт из состояния.
        setMarkerTarget(null);
        dropDraftPin();
        if (created.markerId !== undefined) setFlashNoteId(created.markerId);
        return { ok: true, message: null };
      }
      const noteId = notesUi.open?.noteId;
      if (noteId === undefined) return { ok: false, message: null };
      const result = await workspace.editMarker(fieldId, noteId, {
        note: text,
        happened_on: happenedOn,
      });
      return { ok: result.ok, message: result.message ?? null };
    },
    [dropDraftPin, notesUi, workspace],
  );

  /** Файлы в открытую заметку: у созданной — сразу в очередь, у черновика — в его карман. */
  const addFilesToOpen = useCallback(
    (files: File[]) => {
      const fieldId = workspace.selectedFieldId;
      if (fieldId === null || files.length === 0) return;
      const draft = notesUi.draft;
      if (draft !== null) {
        notesUi.setDraft({ ...draft, files: [...draft.files, ...files] });
        return;
      }
      const noteId = notesUi.open?.noteId;
      if (noteId !== undefined) workspace.uploadPhotos(fieldId, files, noteId);
    },
    [notesUi, workspace],
  );
  /**
   * Какая КАРТОЧКА вспыхивает. Вторая половина ответа на удачную постановку места: флажок
   * мигает на карте, карточка — в консоли, обе `PIN_FLASH_MS`. Одно действие — одна вспышка,
   * просто у неё две поверхности, и человек смотрит ровно на одну из них.
   */
  const [flashNoteId, setFlashNoteId] = useState<number | null>(null);
  useEffect(() => {
    if (flashNoteId === null) return;
    const timer = window.setTimeout(() => setFlashNoteId(null), PIN_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flashNoteId]);

  /**
   * Какое фото открыто во весь экран и чьей заметке оно принадлежит.
   *
   * ЖИВЁТ ЗДЕСЬ, А НЕ В ПАНЕЛИ, потому что входов в окно два и они по разные стороны экрана:
   * лента карточки в консоли и лента в попапе заметки на карте. Общий родитель у них один —
   * этот экран. Он же держит окно открытым через переключение вкладок: портал в `body`
   * переживает размонтирование панели по построению.
   *
   * ОБЛАСТИ БОЛЬШЕ НЕТ (notes-v2): листают ВСЕГДА одну заметку. Галереи всего поля по датам
   * не существует — фотография принадлежит заметке, и «3 / 12» на её ленте из трёх кадров
   * было бы не областью, а поломкой счётчика.
   *
   * Гаснет вместе со сменой поля: фотография принадлежит полю, и окно, пережившее выбор
   * соседнего контура, показывало бы кадр, которого в новой панели нет.
   */
  // Что показывает модалка, живёт в `useNotesUi`: окно обязано пережить переключение вкладки,
  // а панель вместе с ней размонтируется.
  /** Куда лететь по «Показать заметку». `token` — чтобы второе нажатие тоже сработало. */
  const [noteFocus, setNoteFocus] = useState<{
    markerId: number;
    lat: number;
    lon: number;
    token: number;
  } | null>(null);
  /** «Показать на карте» из консоли: кадр по всем заметкам. Кроме токена, ему нечего нести. */
  const [notesFocus, setNotesFocus] = useState<{ token: number } | null>(null);
  useEffect(() => {
    setNoteFocus(null);
    setNotesFocus(null);
  }, [selectedFieldId]);

  // A dropped file's SUCCESS is a notice, not a state of the screen: same four seconds as
  // «Сохранено» in the roster. A FAILURE is not on a timer (drop-notice-sticky): a list of
  // five refusals cannot be read in four seconds, so it stands until «Закрыть» — the button
  // DrawTools puts on the error itself.
  useEffect(() => {
    if (drop.note === null || drop.error !== null) return;
    const timer = window.setTimeout(() => setDrop({ note: null, error: null }), DROP_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [drop]);

  const clearDraft = (): void => {
    workspace.setDraftGeometry(null);
    setClearToken((token) => token + 1);
  };

  // The imagery overlay comes off together with the preview panel while the SEALED form is
  // open: it is the picture the verdict was made from. Only in verification mode — outside
  // it, opening the observation form is not an act of blinding anyone, and taking the map
  // away would just be a screen breaking for no reason.
  const blindingImagery = workspace.verificationMode && workspace.blindOpen;

  /**
   * ЗАМЕТКИ НЕ ПОКАЗЫВАЮТСЯ ПРИ ПЕЧАТИ, и это не перестраховка: фотография стерни отвечает
   * ровно на тот вопрос, который запечатанный режим прячет, — поле убрано или нет. Панель
   * туда и не доходит (полосы вкладок в этом состоянии нет вовсе), но флажки на карте живут
   * отдельной жизнью, и без этого условия они остались бы стоять поверх снимка, который для
   * того и спрятан.
   */
  const notesVisible = workspace.selectedFieldId !== null && !workspace.verdictSealed;

  /** «Показать на карте»: долететь и мигнуть. Модалку НЕ открывает и открытую не закрывает. */
  const flyToNote = (note: { id: number; lat: number | null; lon: number | null }): void => {
    if (note.lat === null || note.lon === null) return;
    setNoteFocus((current) => ({
      markerId: note.id,
      lat: note.lat!,
      lon: note.lon!,
      token: (current?.token ?? 0) + 1,
    }));
  };

  const notesPanel: NotesPanel | null =
    workspace.selectedFieldId === null || !notesVisible
      ? null
      : {
          fieldId: workspace.selectedFieldId,
          notes: workspace.markersOfSelected,
          photos: workspace.photosOfSelected,
          state: workspace.photosState,
          notesState: workspace.markersState,
          notesAtLimit: workspace.markersAtLimit,
          notesLimit: workspace.markerLimit,
          photosAtLimit: workspace.photosAtLimit,
          photoLimit: workspace.photoLimit,
          noteLimit: workspace.notePhotoLimit,
          // Очередь — ЭТОГО поля: строки носят свой `fieldId`, и выбор соседнего контура
          // посреди загрузки не должен показывать чужие файлы в его панели.
          uploads: workspace.photoUploads.filter(
            (row) => row.fieldId === workspace.selectedFieldId,
          ),
          ui: notesUi,
          onUpload: (files, markerId) =>
            workspace.uploadPhotos(workspace.selectedFieldId!, files, markerId),
          onRetryUpload: workspace.retryPhotoUpload,
          onDismissUploads: workspace.dismissPhotoUploads,
          // ОДНА КНОПКА ПЕРЕЧИТЫВАЕТ ОБА СПИСКА: упасть мог любой из них, а человек нажимает
          // «Ещё раз» на панели, а не на том запросе, который он не видел.
          onReload: () => {
            void workspace.loadPhotos(workspace.selectedFieldId!);
            void workspace.loadMarkers(workspace.selectedFieldId!);
          },
          onDeleteNote: (noteId) => workspace.removeMarker(workspace.selectedFieldId!, noteId),
          // Карточка только ОТКРЫВАЕТ: править заметку умеет одна поверхность — модалка.
          onOpen: (noteId, photoId) => notesUi.setOpen({ noteId, photoId, editing: false }),
          onEdit: (noteId) => notesUi.setOpen({ noteId, photoId: null, editing: true }),
          onPlace: (noteId) => {
            const note = workspace.markersOfSelected.find((marker) => marker.id === noteId);
            if (note === undefined) return;
            workspace.setMarkerError(null);
            notesUi.setPlacingNoteId(noteId);
            // Лист тулбара называет, ЧЬЁ место указывают: на карте карточки уже не видно.
            // Имя КОРОТКОЕ (`noteName`): за ним в той же фразе идут ещё два предложения, и
            // полный текст заметки уводил их под пилюлю даты.
            setMarkerTarget({ kind: "place", noteId, what: noteName(note) });
          },
          onShowOnMap: (noteId) => {
            const note = workspace.markersOfSelected.find((marker) => marker.id === noteId);
            if (note !== undefined) flyToNote(note);
          },
          // Кадр по всем заметкам: спрашивают «а где они», а не «где вот эта».
          onShowAllOnMap: () => setNotesFocus((current) => ({ token: (current?.token ?? 0) + 1 })),
          thumbUrl:
            workspace.selectedFieldId === null
              ? undefined
              : (photoId) => api.photoObjectUrl(workspace.selectedFieldId!, photoId, "thumb"),
          flashNoteId,
        };

  /**
   * ЧТО ПОКАЗЫВАЕТ МОДАЛКА. `draft` и `open` — два входа в одно окно: первый описывает
   * заметку, которой ещё нет, второй — существующую. Одновременно они не живут: создание
   * закрывает открытое, и наоборот.
   */
  const creating = notesUi.draft !== null;
  const openNote =
    notesUi.open === null
      ? null
      : (workspace.markersOfSelected.find((marker) => marker.id === notesUi.open!.noteId) ?? null);
  /**
   * ЛЕНТА ОТКРЫТОГО ОКНА — ЧЕРЕЗ `useMemo`, И ЭТО НЕ ОПТИМИЗАЦИЯ, А КОРРЕКТНОСТЬ.
   *
   * Свежий массив на каждый рендер уходил в зависимости эффекта загрузки кадра, тот
   * перезапускался — и уборка предыдущего прогона гасила «жив ли просмотр» для запроса,
   * который ещё летел. Пока родитель перерисовывается часто (прогресс прогона по SSE, очередь
   * загрузки, вспышки), открытый кадр не приезжал вовсе.
   */
  const openMarkerId = notesUi.open?.noteId ?? null;
  const fieldPhotos = workspace.photosOfSelected;
  const openNotePhotos = useMemo(
    () => (openMarkerId === null ? [] : photosOfNote(fieldPhotos, openMarkerId)),
    [fieldPhotos, openMarkerId],
  );
  /**
   * Какой кадр открыт. `null` в состоянии значит «первый»: карточку открывают целиком чаще,
   * чем конкретной ячейкой, и заставлять её знать id первого кадра значило бы дублировать
   * сортировку ленты в двух местах.
   */
  const openPhotoId = notesUi.open?.photoId ?? openNotePhotos[0]?.id ?? null;

  /**
   * ЗАМЕТКА ИСЧЕЗЛА ПОД ОТКРЫТЫМ ОКНОМ — его удалили из другой вкладки. Окно закрывается: его
   * предмет перестал существовать, и держать его открытым значит показывать пустоту.
   */
  useEffect(() => {
    if (notesUi.open === null) return;
    if (workspace.markersOfSelected.some((m) => m.id === notesUi.open!.noteId)) return;
    notesUi.setOpen(null);
  }, [notesUi, workspace.markersOfSelected]);

  // The two corner groups, built here rather than inline: they are the argument of one prop
  // and inlining them would bury the map's own props in eighty lines of toolbar.
  const topLeftControls = (
    <>
      <DrawTools
        mode={mode}
        onModeChange={setMode}
        draft={workspace.draft}
        onClearDraft={clearDraft}
        onNameChange={workspace.setDraftName}
        onSave={() => {
          void workspace.saveDraft().then((created) => {
            if (created) {
              setMode("idle");
              setClearToken((token) => token + 1);
            }
          });
        }}
        saving={false}
        selectedFieldName={workspace.selectedField?.name ?? null}
        onDeleteField={() => {
          if (workspace.selectedFieldId !== null) {
            void workspace.removeField(workspace.selectedFieldId);
          }
        }}
        markerTarget={markerTarget}
        /* Вход с тулбара закрыт тем же гейтом: в запечатанном режиме поверхности заметок нет
           целиком — ни полосы, ни панели, ни флажков, ни кнопки. Путь оператора здесь один, и
           это форма наблюдения. */
        notesEnabled={notesVisible}
        onMarkerMode={(on) => {
          workspace.setMarkerError(null);
          // Тулбар взводит только создание; гасит он ЛЮБОЙ режим — Escape и «выключить» не
          // разбирают, чей режим сейчас взведён, и разбирать не должны.
          notesUi.setPlacingNoteId(null);
          setMarkerTarget(on ? { kind: "new" } : null);
        }}
        markerError={workspace.markerError}
        markersAtLimit={workspace.markersAtLimit}
        markerLimit={workspace.markerLimit}
        importNote={drop.note}
        importError={drop.error}
        onImportDismiss={() => setDrop({ note: null, error: null })}
        maxAreaHa={workspace.serverFlags?.max_field_area_ha ?? undefined}
        smallAreaHa={workspace.serverFlags?.small_field_area_ha ?? undefined}
        onUndo={() => draw.current?.undo()}
        onRedo={() => draw.current?.redo()}
        canUndo={history.canUndo}
        canRedo={history.canRedo}
        issues={check.issues}
      />

      {/* One box, three answers: a pasted pair, one of your own fields by name, or a place
          from the geocoder. The pin it drops is temporary and is not a field. Collapsed to an
          icon — the everyday route in is Ctrl+V, handled above. `near` biases the geocoder
          toward the view for free; it is the viewport that is already published for dates. */}
      <CoordSearch
        onFound={onFound}
        onClear={() => setSearchPoint(null)}
        marked={searchPoint}
        fields={workspace.liveFields}
        onSelectField={workspace.selectField}
        near={viewport?.center ?? null}
      />

      {/* The one map switch. The photograph reads the ground; the street map answers the
          other half of "should I drive there". Nothing else about the map is adjustable. */}
      <BasemapSwitch basemap={basemap} onChange={setBasemap} />
    </>
  );

  // Hidden while the sealed form is open: the freshest accepted date and its imagery are the
  // main entrance to the verdict, and this control picks that date by itself.
  const topRightControls = blindingImagery ? null : (
    <PreviewControl
      calendar={preview.calendar}
      selectedDay={preview.selectedDay}
      selectedDate={preview.selectedDate}
      // `showDate`, not the raw setter: picking a date is the act of wanting to SEE it.
      // The automatic first pick inside usePreview still uses the raw setter, so selecting
      // a field never spends a GEE call on imagery nobody asked for.
      onSelectDate={preview.showDate}
      enabled={preview.enabled}
      onToggle={preview.setEnabled}
      status={preview.status}
      error={preview.error}
      acceptedOnly={preview.acceptedOnly}
      onAcceptedOnly={preview.setAcceptedOnly}
      calendarOpen={preview.calendarOpen}
      onCalendarToggle={preview.setCalendarOpen}
      datesLoading={preview.datesLoading}
      datesError={preview.datesError}
      datesSkipped={preview.datesSkipped}
      // Слой включён, а тайлов не просят: без выбранного поля ниже зума 14 бокса тайлов нет
      // вовсе. Раньше это молчало, и переключатель продолжал утверждать, что снимок показан.
      tilesSkipped={preview.tilesSkipped}
      datesActive={preview.datesActive}
      dev={
        devMode
          ? {
              datesBbox: preview.datesBbox,
              tileBox: preview.tileBox,
              tileUrl: preview.tileUrl,
              cacheHits: preview.tileCacheHits,
              layers: previewLayers,
            }
          : null
      }
    />
  );

  return (
    <div className="k-work">
      <MapCanvas
        theme={theme}
        fields={workspace.liveFields}
        selectedFieldId={workspace.selectedFieldId}
        onSelectField={workspace.selectField}
        mode={mode}
        onDraftChange={workspace.setDraftGeometry}
        clearToken={clearToken}
        previewTileUrl={preview.enabled && !blindingImagery ? preview.tileUrl : null}
        previewBounds={preview.bounds}
        onViewportChange={onViewportChange}
        onPreviewLayers={devMode ? setPreviewLayers : undefined}
        onHistoryChange={onHistoryChange}
        onDrawReady={onDrawReady}
        issuePoints={check.points}
        searchPoint={searchPoint}
        basemap={basemap}
        /* ГЕЙТ ПЕЧАТИ ДЕЙСТВУЕТ И НА ФЛАЖКИ. Он стоял только на фотографиях, а заметки шли
           мимо — и заметка «стерня по колено» оставалась висеть поверх спрятанного снимка.
           Это подсказка оператору, написанная им же: ровно тот вопрос, который запечатанный
           режим и прячет. Пустой массив, а не `display: none`: спрятанный стилем флажок
           по-прежнему берёт клик и открывает попап с текстом. */
        markers={notesVisible ? workspace.markersOfSelected : []}
        markerTarget={markerTarget}
        onExitMarkerMode={() => {
          notesUi.setPlacingNoteId(null);
          setMarkerTarget(null);
        }}
        /* Тык по карте в режиме тулбара: место выбрано, маркер уже стоит, слова спросит
           модалка создания. Карта их больше не спрашивает — редактор у заметки один. */
        /* РЕЖИМ ОСТАЁТСЯ ВЗВЕДЁННЫМ, И ЭТО ДЕРЖИТ СРАЗУ ДВЕ ВЕЩИ.
           Гашение режима здесь снимало эффект карты вместе с его уборкой — а уборка уносит
           маркер-черновик, поэтому единственным откликом на тык оказывалось окно, и клик не
           приземлялся видимо (§4.2). Оно же отнимало вторую попытку: после отказа «дальше
           500 м» приходилось заново жать тулбар. Теперь режим живёт до успеха или Esc, а
           следующий тык просто двигает черновик — он и есть вторая попытка. */
        onDraftPlaced={(point) => {
          // Набранное не теряется: повторный тык двигает МЕСТО, а слова и файлы остаются там,
          // где их набрали.
          const held = notesUi.draft;
          notesUi.setDraft(
            held === null
              ? { text: "", files: [], date: todayIso(), place: point }
              : { ...held, place: point },
          );
        }}
        draftToken={draftToken}
        onOpenNote={(markerId) =>
          notesUi.setOpen({ noteId: markerId, photoId: null, editing: false })
        }
        openNoteId={notesUi.open?.noteId ?? null}
        onPlaceMarker={async (markerId, point) => {
          const fieldId = workspace.selectedFieldId;
          if (fieldId === null) return { ok: false };
          const result = await workspace.editMarker(fieldId, markerId, {
            lon: point.lon,
            lat: point.lat,
          });
          // Удача гасит режим и зажигает карточку; отказ («дальше 500 м») оставляет режим
          // взведённым — целятся ещё раз, поближе, а причина уже стоит в листе тулбара.
          if (result.ok) {
            notesUi.setPlacingNoteId(null);
            setMarkerTarget(null);
            setFlashNoteId(markerId);
          }
          return result;
        }}
        photos={notesVisible ? workspace.photosOfSelected : []}
        photoThumb={
          workspace.selectedFieldId === null
            ? undefined
            : (photoId) => api.photoObjectUrl(workspace.selectedFieldId!, photoId, "thumb")
        }
        noteFocus={noteFocus}
        notesFocus={notesFocus}
        onFileDrop={(file) => {
          // The previous answer goes the moment a new file lands: «создано полей: 2» left
          // standing over an import that has just failed is the wrong sentence twice over.
          setDrop({ note: null, error: null });
          void importDroppedFile(file, workspace.importFeatures).then(setDrop);
        }}
        controls={{ topLeft: topLeftControls, topRight: topRightControls }}
      />

      <FieldList
        fields={workspace.fields}
        loading={workspace.fieldsLoading}
        error={workspace.fieldsError}
        selectedFieldId={workspace.selectedFieldId}
        checkedFieldIds={workspace.checkedFieldIds}
        queue={workspace.queue}
        onSelect={workspace.selectField}
        onToggleCheck={workspace.toggleChecked}
        onCheckAll={workspace.checkAll}
        onClearChecked={workspace.clearChecked}
        onRunBatch={() => void workspace.runBatch(workspace.checkedFieldIds)}
        runBusy={workspace.runState.phase === "waiting" || workspace.runState.phase === "running"}
        onRetry={() => void workspace.loadFields()}
        onUpdateField={workspace.updateField}
        onDeleteField={workspace.removeField}
        actionError={workspace.actionError}
        showArchived={workspace.showArchived}
        onShowArchived={workspace.setShowArchived}
        noteCount={notesVisible ? workspace.markersOfSelected.length : 0}
        // Панель заметок живёт в консоли, за картой, и теперь она ВКЛАДКА: прокручивать
        // больше нечего — вкладку переключают, и список открывается сверху и целиком.
        onShowNotes={() => setNotesTabToken((token) => token + 1)}
      />

      <Console
        field={workspace.selectedField}
        fieldsCount={workspace.liveFields.length}
        fieldsLoading={workspace.fieldsLoading}
        runs={workspace.runsOfSelected}
        activeRun={workspace.activeRun}
        onSelectRun={workspace.selectRun}
        runState={workspace.runState}
        onRun={(flags?: RunFlags) => {
          if (workspace.selectedFieldId === null) return;
          void workspace.runOne(workspace.selectedFieldId, flags ? { flags } : {});
        }}
        onRunBatch={() => void workspace.runBatch(workspace.checkedFieldIds)}
        checkedCount={workspace.checkedFieldIds.length}
        queue={workspace.queue}
        onClearQueue={workspace.clearQueue}
        onSelectField={workspace.selectField}
        flags={workspace.flags}
        onFlagsChange={workspace.setFlags}
        analysisDate={workspace.analysisDate}
        onAnalysisDate={workspace.setAnalysisDate}
        groundTruth={
          workspace.selectedFieldId === null
            ? []
            : (workspace.groundTruth[workspace.selectedFieldId] ?? [])
        }
        onAddGroundTruth={async (input) => {
          if (workspace.selectedFieldId === null) return false;
          const created = await workspace.addGroundTruth(workspace.selectedFieldId, input);
          return created !== null;
        }}
        fieldUse={
          workspace.selectedFieldId === null ? null : workspace.fieldUse[workspace.selectedFieldId]
        }
        fieldUseState={
          workspace.selectedFieldId === null
            ? undefined
            : workspace.fieldUseState[workspace.selectedFieldId]
        }
        onComputeFieldUse={() => {
          if (workspace.selectedFieldId === null) return;
          void workspace.computeFieldUse(workspace.selectedFieldId);
        }}
        verificationMode={workspace.verificationMode}
        verdictSealed={workspace.verdictSealed}
        onReveal={workspace.revealRun}
        blindOpen={workspace.blindOpen}
        onBlindOpen={workspace.setBlindOpen}
        revealToken={workspace.revealToken}
        animate={animate}
        onPickDate={preview.showDate}
        // Полоса состояния помечает показанную дату и печатает её словами: иначе стрелки
        // ←/→ меняют снимок, а полоса про это молчит, и сравнить две даты можно только мышью.
        previewDate={preview.selectedDate}
        actionError={workspace.actionError}
        notes={notesPanel}
        notesTabToken={notesTabToken}
      />

      {/* ОКНО ЖИВЁТ ЗДЕСЬ, А НЕ В ПАНЕЛИ, и это то, что даёт ему пережить переключение
          вкладки: панель «Заметки» размонтируется вместе с вкладкой, а портал в `body` —
          нет. Открытым оно накрывает консоль целиком, фокус заперт внутри. */}
      {/* ОКНО ЖИВЁТ ЗДЕСЬ, А НЕ В ПАНЕЛИ: панель «Заметки» размонтируется вместе с вкладкой, а
          портал в `body` — нет. Открытым оно накрывает консоль целиком, фокус заперт внутри. */}
      {workspace.selectedFieldId !== null && (creating || openNote !== null) && (
        <NoteModal
          fieldId={workspace.selectedFieldId}
          note={creating ? null : openNote}
          photos={creating ? [] : openNotePhotos}
          openId={creating ? null : openPhotoId}
          onOpenIdChange={(photoId) =>
            notesUi.setOpen({ noteId: openMarkerId!, photoId, editing: false })
          }
          onClose={() => {
            notesUi.setDraft(null);
            notesUi.setEditing(null);
            notesUi.setOpen(null);
            setModalEditing(null);
            /**
             * ЧЕРНОВИК УХОДИТ, А РЕЖИМ ОСТАЁТСЯ — и это ровно то, что даёт вторую попытку.
             *
             * Точки, которую никто не сохранил, на карте быть не должно, поэтому маркер
             * снимается. Но режим гасить здесь нельзя: человек, получивший «дальше 500 м»,
             * закрывает окно РАДИ ТОГО, чтобы ткнуть ближе, — и гашение заставило бы его
             * заново искать кнопку тулбара. Гасят режим ровно двое: удачное сохранение и Esc
             * (его ловит лист тулбара, когда модалки уже нет).
             */
            if (creating) dropDraftPin();
          }}
          editing={modalEditing}
          onEditing={setModalEditing}
          onSave={saveNote}
          draftFiles={notesUi.draft?.files ?? []}
          onAddFiles={addFilesToOpen}
          /* Состав накопленных файлов целиком — это крестик на ячейке. Текст и дата черновика
             при этом не трогаются: убрали кадр, а не начали заново. */
          onDraftFiles={(files) => {
            const draft = notesUi.draft;
            if (draft !== null) notesUi.setDraft({ ...draft, files });
          }}
          photosFull={
            !creating &&
            (workspace.photosAtLimit || openNotePhotos.length >= workspace.notePhotoLimit)
          }
          fullReason={
            workspace.photosAtLimit
              ? T.photos.limitHint(workspace.photoLimit)
              : openNotePhotos.length >= workspace.notePhotoLimit
                ? T.photos.limitNote(workspace.notePhotoLimit)
                : null
          }
          onDeleteNote={async () => {
            if (creating) {
              notesUi.setDraft(null);
              setMarkerTarget(null);
              dropDraftPin();
              return { ok: true, message: null };
            }
            const result = await workspace.removeMarker(workspace.selectedFieldId!, openMarkerId!);
            if (result.ok) notesUi.setOpen(null);
            return { ok: result.ok, message: result.message ?? null };
          }}
          onDeletePhoto={async (photoId) => {
            const result = await workspace.removePhoto(workspace.selectedFieldId!, photoId);
            return { ok: result.ok, message: result.message ?? null };
          }}
          onMovePhoto={async (photoId, markerId) => {
            const result = await workspace.editPhoto(workspace.selectedFieldId!, photoId, {
              marker_id: markerId,
            });
            return { ok: result.ok, message: result.message ?? null };
          }}
          /* МЕСТО ПРАВИТСЯ НЕ ЗДЕСЬ: тыкать надо в карту, а карта под модалкой. Окно
             закрывается и взводит place-режим; удачный тык обратно его НЕ открывает —
             человек только что смотрел на карту, и окно, прыгнувшее ему в лицо в ответ на
             его же клик, отбирает тот самый вид, ради которого он целился. */
          onPlace={() => {
            const note = openNote;
            notesUi.setOpen(null);
            if (note === null) return;
            workspace.setMarkerError(null);
            notesUi.setPlacingNoteId(note.id);
            setMarkerTarget({ kind: "place", noteId: note.id, what: noteName(note) });
          }}
          onClearPlace={async () => {
            const result = await workspace.editMarker(workspace.selectedFieldId!, openMarkerId!, {
              lon: null,
              lat: null,
            });
            return { ok: result.ok, message: result.message ?? null };
          }}
          notes={workspace.markersOfSelected}
          fieldPhotos={workspace.photosOfSelected}
          thumbUrl={(photoId) => api.photoObjectUrl(workspace.selectedFieldId!, photoId, "thumb")}
        />
      )}
    </div>
  );
}
